<template>
  <BContentBox v-loading="loading">
    <template #content>
      <div class="content-container">
        <div class="section">
          <div class="title">
            {{ $t('transcriptionSummarizationPromptSetting.format.title') }}
          </div>
          <div class="explain">
            {{ $t('transcriptionSummarizationPromptSetting.format.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('transcriptionSummarizationPromptSetting.format.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('transcriptionSummarizationPromptSetting.format.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="outputFormat"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>
        <div class="section">
          <div class="title">
            {{ $t('transcriptionSummarizationPromptSetting.formatDescription.title') }}
          </div>
          <div class="explain">
            {{ $t('transcriptionSummarizationPromptSetting.formatDescription.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('transcriptionSummarizationPromptSetting.formatDescription.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('transcriptionSummarizationPromptSetting.formatDescription.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="outputFormatDescription"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>

        <BLayout>
          <BBtn
            type="primary"
            :disabled="loading"
            @click="handleSaveClick"
          >
            {{ $t('general.save.text') }}
          </BBtn>
        </BLayout>
      </div>
    </template>
  </BContentBox>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { TranscriptionSummaryPromptSetting } from '@/api/openapi';
import { PromptSettingApiService } from '@/api/user/resources/prompt_setting';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_TOAST } from '@/injection-keys';

const i18n = useI18n();
const { doActionWithWait, wait } = useWait();
const toast = inject(KEY_OF_TOAST);

const transcriptionSummaryPromptSetting = ref<TranscriptionSummaryPromptSetting>();
const outputFormat = ref<string>();
const outputFormatDescription = ref<string>();
const loading = computed(() => wait.is(['fetchPromptSettingWait', 'putPromptSettingWait']));

const fetchPromptSetting = () => {
  doActionWithWait('fetchPromptSettingWait', async () => {
    const api = new PromptSettingApiService();
    const { data } = await api.getTranscriptionSummaryPromptSetting({
      errorHandlers: {
        404: () => {},
      },
    });

    transcriptionSummaryPromptSetting.value = data.promptSetting;
    outputFormat.value = data.promptSetting.outputFormat;
    outputFormatDescription.value = data.promptSetting.outputFormatDescription;
  });
};
onBeforeMount(() => {
  fetchPromptSetting();
});

const handleSaveClick = async () => {
  doActionWithWait('putPromptSettingWait', async () => {
    const api = new PromptSettingApiService();
    await api.putTranscriptionSummaryPromptSetting({
      request: {
        putTranscriptionSummaryPromptSettingRequest: {
          promptSetting: {
            outputFormat: outputFormat.value,
            outputFormatDescription: outputFormatDescription.value,
          },
        },
      },
    });
  });
  toast.show(
    i18n.t('general.done.withTarget', {
      action: i18n.t('general.save.text'),
      target: i18n.t('transcriptionSummarizationPromptSetting.title'),
    }),
  );
};
</script>

<style lang="scss" scoped>
.content-container {
  max-width: 900px;
}
.title {
  font-size: $fontsize-400;
  font-weight: bold;
  margin-bottom: $basespace-150;
}
.explain {
  color: $textcolor-light;
  white-space: pre-wrap;
  margin-bottom: $basespace-400;
}
.example {
  margin-bottom: $basespace-200;
}
.example :deep(*){
  color: $textcolor-light;
}
.form {
  width: 100%;
}
.example-content {
  @include m-break-word-pre-wrap
}
</style>
